import clsx from 'clsx';
import type { ButtonHTMLAttributes, ReactNode, ReactText } from 'react';
import React, { AriaAttributes, forwardRef, HTMLAttributeAnchorTarget, HTMLAttributeReferrerPolicy } from 'react';

import { Disallow } from '../utils/types';
import { Icon, IconProps } from './Icon';
import { BodyText, BodyTextProps } from './Typography/BodyText';

export type ButtonProps = BaseButtonProps &
  (
    | (Disallow<ButtonIconProps> & Disallow<ButtonLinkProps>)
    | (ButtonIconProps & Disallow<ButtonLinkProps>)
    | (Disallow<ButtonIconProps> & ButtonLinkProps)
    | (ButtonIconProps & ButtonLinkProps)
  );

export type BaseButtonProps = {
  /**
   * Represents the button style and appearance.
   */
  variant?: 'primary' | 'secondary' | 'ghost' | 'destructive' | 'link';
  /**
   * Is the button disabled? Different styles apply when in a disabled state.
   */
  disabled?: boolean;
  /**
   * Whether the Button should flex grow or not
   */
  grow?: boolean;
  /**
   * Content to be rendered inside the button
   */
  children?: ReactText;
  /**
   * Remove the button from the tabbing order of the webpage. This may be used
   * to prevent extra keyboard focus stops for cases like an input with a clear
   * button.
   */
  noTab?: boolean;
  /**
   * The type of the button. Possible values are:
   *
   * * `submit`: The button submits the form data to the server. This is the
   *   default if the attribute is not specified, or if the attribute is
   *   dynamically changed to an empty or invalid value.
   * * `reset`: The button resets the form.
   * * `button`: The button has no default behavior. It can have client-side
   *   scripts associated with the element's events, which are triggered when
   *   the events occur.
   * * `menu`: The button displays a menu.
   */
  type?: ButtonHTMLAttributes<HTMLButtonElement>['type'];
  /**
   * Whether the button will span the width of it's container
   *
   * @defaultValue false
   */
  fullWidth?: boolean;
  /**
   * Adds the html title attribute to the button element (native tooltip)
   */
  title?: string;
  /**
   * Adds the aria-label attribute for accessibility
   * Should describe the function of the button
   */
  ariaLabel?: string;
  /**
   * Click handler triggered for the button.
   */
  onClick?: React.MouseEventHandler<HTMLButtonElement | HTMLAnchorElement>;
};

export type ButtonLinkProps = {
  /**
   * If href is present the root node will be <a> instead of <button>
   */
  href?: string;

  /**
   * Specifies the relationship between the current document and the linked document
   */
  rel?: string;

  /**
   * Specifies where to open the linked document
   */
  target?: HTMLAttributeAnchorTarget;

  /**
   * Specifies which referrer information to send with the link
   */
  referrerpolicy?: HTMLAttributeReferrerPolicy;

  /**
   * Specifies a space-separated list of URLs to which, when the link is followed, post requests with the body ping will be sent by the browser (in the background). Typically used for tracking.
   */
  ping?: string;
};

type ButtonIconProps = {
  /** hide from screen readers */
  iconAriaHidden?: AriaAttributes['aria-hidden'];
  /** name of the icon to use */
  icon: IconProps['name'];
  /** position of the icon */
  iconPos?: 'left' | 'right';
  /** size of the icon, defaults to 'medium' */
  iconSize?: IconProps['size'];
  /** id of the title element in the rendered svg */
  iconTitleId?: IconProps['titleId'];
};

const variantToTextColor: Partial<Record<NonNullable<ButtonProps['variant']>, BodyTextProps['color']>> = {
  primary: 'white',
  secondary: 'black',
  ghost: 'black',
  destructive: 'red',
  link: 'black',
};
const variantToIconColor: Partial<Record<NonNullable<ButtonProps['variant']>, IconProps['color']>> = {
  primary: 'white',
  secondary: 'black',
  ghost: 'black',
  destructive: 'red',
  link: 'black',
};

const ButtonWithRef = (
  {
    variant = 'primary',
    type = 'button',
    grow,
    disabled = false,
    fullWidth = false,
    title,
    iconAriaHidden,
    ariaLabel,
    onClick,
    children,
    icon,
    iconPos = 'left',
    iconSize,
    iconTitleId,
    href,
    noTab,
    rel,
    target,
    referrerpolicy,
    ping,
    ...restProps
  }: ButtonProps,
  ref: React.Ref<HTMLButtonElement>,
): JSX.Element => {
  const typographyColor = variantToTextColor[variant];
  let iconColor: IconProps['color'];
  let iconElement: ReactNode;
  if (icon) {
    iconColor = variantToIconColor[variant];
    iconElement = (
      <Icon ariaHidden={iconAriaHidden} name={icon} size={iconSize} titleId={iconTitleId} color={iconColor} />
    );
  }

  const btnClasses = clsx(
    `inline-flex justify-center outline-none disabled:pointer-events-none items-center`,
    {
      'hover:bg-button-nonprimary-hover active:bg-button-nonprimary-active': variant !== 'primary',
      'rounded border py-12 ring-offset focus-visible:ring': variant !== 'link',
      'px-24': variant !== 'link' && children,
      'px-12': variant !== 'link' && !children,
      'space-x-8': icon && children,
    },
    {
      'border-green bg-primary-green hover:border-button-primary-hover hover:bg-button-primary-hover active:border-button-primary-active active:bg-button-primary-active':
        variant === 'primary',
      'border bg-white': variant === 'secondary',
      'border-red bg-white': variant === 'destructive',
      'border-none bg-transparent focus-visible:border-black': variant === 'ghost',
      'border-2 border-transparent bg-transparent p-[2px] focus-visible:rounded-4 focus-visible:border-black':
        variant === 'link',
      'opacity-50': disabled,
      'flex-grow': grow,
    },
    {
      'w-full': fullWidth,
    },
  );

  const content = (
    <>
      {iconPos === 'left' ? iconElement : null}
      <BodyText bold textTransform="capitalize" underline={variant === 'link'} color={typographyColor}>
        {children}
      </BodyText>
      {iconPos === 'right' ? iconElement : null}
    </>
  );

  if (href) {
    return (
      <a
        href={href}
        onClick={onClick}
        title={title}
        aria-label={ariaLabel}
        rel={rel}
        tabIndex={noTab ? -1 : 0}
        target={target}
        referrerPolicy={referrerpolicy}
        ping={ping}
        className={clsx(btnClasses, 'hover:no-underline focus:no-underline active:no-underline', {
          'pointer-events-none': disabled,
        })}
      >
        {content}
      </a>
    );
  }

  return (
    <button
      type={type}
      onClick={onClick}
      title={title}
      aria-label={ariaLabel}
      disabled={disabled}
      className={btnClasses}
      ref={ref}
      tabIndex={noTab ? -1 : 0}
      {...restProps}
    >
      {content}
    </button>
  );
};

export const Button = forwardRef(ButtonWithRef);
